<template>
  <div 
    class="premium-editor-container"
    :class="[
      `theme-${theme}`,
      editorClass
    ]"
  >
    <!-- Loading State -->
    <div v-if="!isEditorReady && !editorError" class="editor-loading">
      <div class="loading-spinner"></div>
      <p>Loading Premium Editor...</p>
    </div>

    <!-- Error State -->
    <div v-if="editorError" class="editor-error">
      <div class="error-icon">⚠️</div>
      <div class="error-content">
        <h3>Editor Failed to Load</h3>
        <div v-if="missingDependencies.length > 0" class="missing-deps">
          <p><strong>Missing Dependencies:</strong></p>
          <ul>
            <li v-for="dep in missingDependencies" :key="dep">{{ dep }}</li>
          </ul>
          <div class="install-command">
            <p><strong>Quick Fix:</strong></p>
            <code>npm install @vue/composition-api @tiptap/core @tiptap/vue-2 @tiptap/starter-kit @tiptap/extension-text-style @tiptap/extension-color @tiptap/extension-highlight @tiptap/extension-underline @tiptap/extension-text-align @tiptap/extension-link @tiptap/extension-font-family @tiptap/extension-code @tiptap/extension-code-block @tiptap/extension-table @tiptap/extension-table-row @tiptap/extension-table-cell @tiptap/extension-table-header @tiptap/extension-task-list @tiptap/extension-task-item @tiptap/extension-placeholder</code>
          </div>
        </div>
        <div v-else class="generic-error">
          <p>{{ editorError }}</p>
          <p>Please check the browser console for more details.</p>
        </div>
        <div class="help-links">
          <a href="https://github.com/Mahadi74/vue2-premium-bbl-editor#quick-setup" target="_blank">📖 Setup Guide</a>
          <a href="https://github.com/Mahadi74/vue2-premium-bbl-editor/blob/main/TROUBLESHOOTING.md" target="_blank">🔧 Troubleshooting</a>
        </div>
      </div>
    </div>

    <!-- Main Editor -->
    <div v-show="isEditorReady && !editorError" class="premium-editor-wrapper">
      <!-- Toolbar -->
      <div 
        v-if="showToolbar" 
        class="premium-toolbar-container"
        :class="toolbarClass"
      >
        <ToolbarMain
          v-if="editor"
          :editor="editor"
          :config="toolbarConfig"
          :font-families="fontFamilies"
          :font-sizes="fontSizes"
          :line-heights="lineHeights"
          :allowed-headings="allowedHeadings"
          :source-code-mode="isSourceCodeMode"
          @execute-command="executeCommand"
          @insert-link="openLinkModal"
          @insert-image="showImageModal = true"
          @insert-video="showVideoModal = true"
          @clear-formatting="clearFormatting"
          @toggle-source-code="toggleSourceCode"
        />
        <div v-else class="toolbar-loading">
          <div class="toolbar-loading-content">
            <div class="loading-spinner-small"></div>
            <span>Loading toolbar...</span>
          </div>
        </div>
      </div>

      <!-- Editor Content Area -->
      <div 
        class="premium-editor-content"
        :class="contentClass"
        :style="contentStyles"
      >
        <!-- Visual Editor -->
        <editor-content 
          v-show="!isSourceCodeMode"
          :editor="editor" 
          class="bbl-editor-area"
          :style="editorStyles"
        />
        
        <!-- Source Code Editor -->
        <textarea
          v-show="isSourceCodeMode"
          v-model="sourceCode"
          @input="updateFromSourceCode"
          class="source-code-editor"
          :style="editorStyles"
          :spellcheck="spellcheck"
        ></textarea>
      </div>

      <!-- Bubble Menus -->
      <EditorBubbleMenu
        v-if="showBubbleMenu && editor"
        :editor="editor"
        :config="toolbarConfig"
        @execute-command="executeCommand"
      />

      <TableBubbleMenu
        v-if="showTableBubbleMenu && editor"
        :editor="editor"
      />

      <ImageBubbleMenu
        v-if="showImageBubbleMenu && editor"
        :editor="editor"
      />

      <VideoBubbleMenu
        v-if="showVideoBubbleMenu && editor"
        :editor="editor"
      />
    </div>

    <!-- Modals -->
    <LinkModal
      :visible="showLinkModal"
      :initial-text="linkModalData.text"
      :initial-url="linkModalData.url"
      :initial-target="linkModalData.target"
      @close="showLinkModal = false"
      @insert="insertLink"
      @remove="removeLink"
    />

    <ImageModal
      :visible="showImageModal"
      :upload-handler="uploadHandler || defaultImageUpload"
      :upload-manager="uploadManager"
      :max-file-size="maxFileSize"
      :allowed-types="allowedImageTypes"
      @close="showImageModal = false"
      @insert="insertImage"
    />

    <VideoModal
      :visible="showVideoModal"
      :upload-handler="uploadHandler || defaultVideoUpload"
      :upload-manager="uploadManager"
      :max-file-size="maxFileSize"
      :allowed-types="allowedVideoTypes"
      @close="showVideoModal = false"
      @insert="insertVideo"
    />
  </div>
</template>

<script>
import { EditorContent } from '@tiptap/vue-2'
import { useEditor } from '../composables/useEditor'
import ToolbarMain from './Toolbar/ToolbarMain.vue'
import EditorBubbleMenu from './Menus/BubbleMenu.vue'
import TableBubbleMenu from './Menus/TableBubbleMenu.vue'
import ImageBubbleMenu from './Menus/ImageBubbleMenu.vue'
import VideoBubbleMenu from './Menus/VideoBubbleMenu.vue'
import ImageModal from './Modals/ImageModal.vue'
import VideoModal from './Modals/VideoModal.vue'
import LinkModal from './Modals/LinkModal.vue'

// Import styles
import '../styles/editor.css'
import '../styles/toolbar.css'
import '../styles/tables.css'

export default {
  name: 'PremiumBblEditor',
  components: {
    EditorContent,
    ToolbarMain,
    EditorBubbleMenu,
    TableBubbleMenu,
    ImageBubbleMenu,
    VideoBubbleMenu,
    ImageModal,
    VideoModal,
    LinkModal,
  },
  props: {
    // Content and basic configuration
    value: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: 'Start writing...'
    },
    editable: {
      type: Boolean,
      default: true
    },
    outputFormat: {
      type: String,
      default: 'html',
      validator: (value) => ['html', 'json'].includes(value)
    },
    
    // Layout and appearance
    maxHeight: {
      type: [String, Number],
      default: null
    },
    minHeight: {
      type: [String, Number],
      default: 200
    },
    theme: {
      type: String,
      default: 'default',
      validator: (value) => ['default', 'minimal', 'dark'].includes(value)
    },
    
    // UI Components visibility
    showToolbar: {
      type: Boolean,
      default: true
    },
    showBubbleMenu: {
      type: Boolean,
      default: true
    },
    showTableBubbleMenu: {
      type: Boolean,
      default: true
    },
    showImageBubbleMenu: {
      type: Boolean,
      default: true
    },
    showVideoBubbleMenu: {
      type: Boolean,
      default: true
    },
    
    // Toolbar configuration
    toolbarConfig: {
      type: Object,
      default: () => ({
        // Text formatting
        bold: true,
        italic: true,
        underline: true,
        strike: true,
        code: true,
        
        // Text styling
        textColor: true,
        highlight: true,
        fontFamily: true,
        fontSize: true,
        
        // Alignment and spacing
        textAlign: true,
        lineHeight: true,
        
        // Structure
        headings: true,
        lists: true,
        blockquote: true,
        codeBlock: true,
        horizontalRule: true,
        
        // Media
        link: true,
        image: true,
        video: true,
        table: true,
        
        // Utilities
        clearFormatting: true,
        sourceCode: true,
        undo: true,
        redo: true
      })
    },
    
    // Extension configuration
    extensionConfig: {
      type: Object,
      default: () => ({
        // Image settings
        image: {
          allowResize: true,
          allowAlignment: true,
          allowDelete: true,
          maxWidth: '100%',
          quality: 0.8
        },
        
        // Video settings
        video: {
          allowResize: true,
          allowAlignment: true,
          allowDelete: true,
          maxWidth: '100%',
          autoplay: false
        },
        
        // Table settings
        table: {
          resizable: true,
          allowRowControls: true,
          allowColumnControls: true,
          cellSelection: true
        },
        
        // Link settings
        link: {
          openOnClick: false,
          autolink: true,
          linkOnPaste: true
        },
        
        // Text settings
        textAlign: {
          types: ['heading', 'paragraph']
        }
      })
    },
    
    // Upload and media handling
    uploadHandler: {
      type: Function,
      default: null
    },
    uploadManager: {
      type: Object,
      default: null
    },
    imageUploadUrl: {
      type: String,
      default: null
    },
    videoUploadUrl: {
      type: String,
      default: null
    },
    maxFileSize: {
      type: Number,
      default: 10 * 1024 * 1024 // 10MB
    },
    allowedImageTypes: {
      type: Array,
      default: () => ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
    },
    allowedVideoTypes: {
      type: Array,
      default: () => ['video/mp4', 'video/webm', 'video/ogg']
    },
    
    // Local file base64 management
    useBase64Upload: {
      type: Boolean,
      default: false
    },
    base64Quality: {
      type: Number,
      default: 0.8,
      validator: (value) => value >= 0.1 && value <= 1.0
    },
    base64MaxWidth: {
      type: Number,
      default: 1920
    },
    base64MaxHeight: {
      type: Number,
      default: 1080
    },
    enableImageCompression: {
      type: Boolean,
      default: true
    },
    preserveOriginalFileName: {
      type: Boolean,
      default: true
    },
    base64Prefix: {
      type: String,
      default: 'data:'
    },
    
    // Content restrictions
    maxContentLength: {
      type: Number,
      default: null
    },
    allowedHeadings: {
      type: Array,
      default: () => [1, 2, 3, 4, 5, 6]
    },
    
    // Styling options
    fontFamilies: {
      type: Array,
      default: () => [
        'Inter',
        'Arial',
        'Helvetica',
        'Times New Roman',
        'Georgia',
        'Courier New',
        'Monaco',
        'Menlo'
      ]
    },
    fontSizes: {
      type: Array,
      default: () => ['12px', '14px', '16px', '18px', '20px', '24px', '28px', '32px', '36px', '48px']
    },
    lineHeights: {
      type: Array,
      default: () => ['1', '1.2', '1.4', '1.6', '1.8', '2', '2.5', '3']
    },
    
    // Behavior options
    autofocus: {
      type: Boolean,
      default: false
    },
    spellcheck: {
      type: Boolean,
      default: true
    },
    autoSave: {
      type: Boolean,
      default: false
    },
    autoSaveInterval: {
      type: Number,
      default: 30000 // 30 seconds
    },
    
    // Custom CSS classes
    editorClass: {
      type: [String, Array, Object],
      default: ''
    },
    toolbarClass: {
      type: [String, Array, Object],
      default: ''
    },
    contentClass: {
      type: [String, Array, Object],
      default: ''
    }
  },
  setup(props, { emit }) {
    const {
      editor,
      isEditorReady,
      currentContent,
      editorError,
      missingDependencies,
      executeCommand,
      isActive,
      getContent,
      setContent,
      focus,
      blur
    } = useEditor(props, emit)

    return {
      editor,
      isEditorReady,
      currentContent,
      editorError,
      missingDependencies,
      executeCommand,
      isActive,
      getContent,
      setContent,
      focus,
      blur
    }
  },
  data() {
    return {
      showImageModal: false,
      showVideoModal: false,
      showLinkModal: false,
      linkModalData: {
        text: '',
        url: '',
        target: ''
      },
      isSourceCodeMode: false,
      sourceCode: '',
      sourceCodeUpdateTimeout: null,
      autoSaveTimeout: null
    }
  },
  computed: {
    contentStyles() {
      const styles = {}
      if (this.minHeight) {
        styles.minHeight = typeof this.minHeight === 'number' ? `${this.minHeight}px` : this.minHeight
      }
      return styles
    },
    editorStyles() {
      const styles = {}
      if (this.maxHeight) {
        styles.maxHeight = typeof this.maxHeight === 'number' ? `${this.maxHeight}px` : this.maxHeight
      }
      if (this.minHeight) {
        styles.minHeight = typeof this.minHeight === 'number' ? `${this.minHeight}px` : this.minHeight
      }
      return styles
    }
  },
  watch: {
    // Auto-save functionality
    currentContent: {
      handler(newContent) {
        if (this.autoSave && newContent !== this.value) {
          this.scheduleAutoSave()
        }
      },
      immediate: false
    }
  },
  beforeDestroy() {
    if (this.sourceCodeUpdateTimeout) {
      clearTimeout(this.sourceCodeUpdateTimeout)
    }
    if (this.autoSaveTimeout) {
      clearTimeout(this.autoSaveTimeout)
    }
  },
  methods: {
    // Auto-save functionality
    scheduleAutoSave() {
      if (this.autoSaveTimeout) {
        clearTimeout(this.autoSaveTimeout)
      }
      
      this.autoSaveTimeout = setTimeout(() => {
        this.$emit('auto-save', this.currentContent)
      }, this.autoSaveInterval)
    },
    
    // Default upload handlers
    defaultImageUpload(file) {
      return new Promise((resolve, reject) => {
        if (this.imageUploadUrl && !this.useBase64Upload) {
          // Use provided upload URL
          const formData = new FormData()
          formData.append('image', file)
          
          fetch(this.imageUploadUrl, {
            method: 'POST',
            body: formData
          })
          .then(response => response.json())
          .then(data => {
            if (data.url) {
              resolve({ src: data.url, alt: file.name })
            } else {
              reject(new Error('Upload failed: No URL returned'))
            }
          })
          .catch(reject)
        } else {
          // Use base64 conversion with compression
          this.convertImageToBase64(file)
            .then(result => resolve(result))
            .catch(reject)
        }
      })
    },
    
    defaultVideoUpload(file) {
      return new Promise((resolve, reject) => {
        if (this.videoUploadUrl && !this.useBase64Upload) {
          // Use provided upload URL
          const formData = new FormData()
          formData.append('video', file)
          
          fetch(this.videoUploadUrl, {
            method: 'POST',
            body: formData
          })
          .then(response => response.json())
          .then(data => {
            if (data.url) {
              resolve({ src: data.url })
            } else {
              reject(new Error('Upload failed: No URL returned'))
            }
          })
          .catch(reject)
        } else {
          // Use base64 conversion for videos
          this.convertVideoToBase64(file)
            .then(result => resolve(result))
            .catch(reject)
        }
      })
    },
    
    // Enhanced base64 conversion with compression for images
    convertImageToBase64(file) {
      return new Promise((resolve, reject) => {
        if (!this.enableImageCompression) {
          // Simple base64 conversion without compression
          const reader = new FileReader()
          reader.onload = (e) => {
            resolve({
              src: e.target.result,
              alt: this.preserveOriginalFileName ? file.name : 'Image',
              size: file.size,
              type: file.type,
              originalName: file.name
            })
          }
          reader.onerror = reject
          reader.readAsDataURL(file)
          return
        }

        // Create image element for compression
        const img = new Image()
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')
        
        img.onload = () => {
          try {
            // Calculate new dimensions while maintaining aspect ratio
            let { width, height } = this.calculateImageDimensions(
              img.width, 
              img.height, 
              this.base64MaxWidth, 
              this.base64MaxHeight
            )
            
            // Set canvas dimensions
            canvas.width = width
            canvas.height = height
            
            // Draw and compress image
            ctx.drawImage(img, 0, 0, width, height)
            
            // Convert to base64 with quality setting
            const compressedBase64 = canvas.toDataURL(file.type, this.base64Quality)
            
            // Emit compression event
            this.$emit('image-compressed', {
              originalSize: file.size,
              compressedSize: this.getBase64Size(compressedBase64),
              originalDimensions: { width: img.width, height: img.height },
              compressedDimensions: { width, height },
              compressionRatio: file.size / this.getBase64Size(compressedBase64)
            })
            
            resolve({
              src: compressedBase64,
              alt: this.preserveOriginalFileName ? file.name : 'Image',
              width: width,
              height: height,
              size: this.getBase64Size(compressedBase64),
              originalSize: file.size,
              type: file.type,
              originalName: file.name,
              compressed: true
            })
          } catch (error) {
            reject(new Error(`Image compression failed: ${error.message}`))
          }
        }
        
        img.onerror = () => reject(new Error('Failed to load image for compression'))
        
        // Create object URL for image loading
        const objectUrl = URL.createObjectURL(file)
        img.src = objectUrl
        
        // Clean up object URL after loading
        img.onload = (originalOnload => function() {
          URL.revokeObjectURL(objectUrl)
          return originalOnload.apply(this, arguments)
        })(img.onload)
      })
    },
    
    // Base64 conversion for videos (no compression)
    convertVideoToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = (e) => {
          resolve({
            src: e.target.result,
            size: file.size,
            type: file.type,
            originalName: this.preserveOriginalFileName ? file.name : 'Video',
            compressed: false
          })
        }
        reader.onerror = reject
        reader.readAsDataURL(file)
      })
    },
    
    // Calculate optimal image dimensions
    calculateImageDimensions(originalWidth, originalHeight, maxWidth, maxHeight) {
      let width = originalWidth
      let height = originalHeight
      
      // Calculate scaling factor
      const widthRatio = maxWidth / originalWidth
      const heightRatio = maxHeight / originalHeight
      const scalingFactor = Math.min(widthRatio, heightRatio, 1) // Don't upscale
      
      if (scalingFactor < 1) {
        width = Math.round(originalWidth * scalingFactor)
        height = Math.round(originalHeight * scalingFactor)
      }
      
      return { width, height }
    },
    
    // Get approximate size of base64 string
    getBase64Size(base64String) {
      // Remove data URL prefix to get actual base64 data
      const base64Data = base64String.split(',')[1] || base64String
      // Base64 encoding increases size by ~33%, so decode size is ~75% of encoded size
      return Math.round((base64Data.length * 3) / 4)
    },
    
    // Content validation
    validateContent(content) {
      if (this.maxContentLength && content.length > this.maxContentLength) {
        this.$emit('content-limit-exceeded', {
          current: content.length,
          max: this.maxContentLength
        })
        return false
      }
      return true
    },
    // HTML Formatter - formats HTML for better readability
    formatHTML(html) {
      if (!html) return ''
      
      let formatted = ''
      let indent = 0
      const tab = '  ' // 2 spaces
      
      // Inline elements that shouldn't cause line breaks
      const inlineElements = ['a', 'abbr', 'b', 'bdi', 'bdo', 'br', 'cite', 'code', 'data', 'dfn', 
                              'em', 'i', 'kbd', 'mark', 'q', 's', 'samp', 'small', 'span', 
                              'strong', 'sub', 'sup', 'time', 'u', 'var']
      
      // Self-closing tags
      const selfClosing = ['br', 'hr', 'img', 'input', 'link', 'meta']
      
      // Parse HTML into tokens
      const tokens = []
      let pos = 0
      
      while (pos < html.length) {
        // Find next tag
        const tagStart = html.indexOf('<', pos)
        
        if (tagStart === -1) {
          // No more tags, add remaining text
          const text = html.substring(pos).trim()
          if (text) tokens.push({ type: 'text', content: text })
          break
        }
        
        // Add text before tag
        if (tagStart > pos) {
          const text = html.substring(pos, tagStart).trim()
          if (text) tokens.push({ type: 'text', content: text })
        }
        
        // Find tag end
        const tagEnd = html.indexOf('>', tagStart)
        if (tagEnd === -1) break
        
        const tag = html.substring(tagStart, tagEnd + 1)
        const tagName = tag.match(/<\/?(\w+)/) && tag.match(/<\/?(\w+)/)[1] && tag.match(/<\/?(\w+)/)[1].toLowerCase()
        
        if (tag.startsWith('</')) {
          tokens.push({ type: 'close', content: tag, tagName, inline: inlineElements.includes(tagName) })
        } else if (tag.endsWith('/>') || selfClosing.includes(tagName)) {
          tokens.push({ type: 'self', content: tag, tagName })
        } else if (tag.startsWith('<!--')) {
          tokens.push({ type: 'comment', content: tag })
        } else {
          tokens.push({ type: 'open', content: tag, tagName, inline: inlineElements.includes(tagName) })
        }
        
        pos = tagEnd + 1
      }
      
      // Format tokens
      let inInline = false
      let lineBuffer = ''
      
      tokens.forEach((token, index) => {
        const nextToken = tokens[index + 1]
        const prevToken = tokens[index - 1]
        
        if (token.type === 'open') {
          if (token.inline) {
            // Inline element - don't add newline
            if (!inInline && lineBuffer) {
              formatted += tab.repeat(indent) + lineBuffer
              lineBuffer = ''
            }
            lineBuffer += token.content
            inInline = true
          } else {
            // Block element
            if (lineBuffer) {
              formatted += tab.repeat(indent) + lineBuffer + '\n'
              lineBuffer = ''
            }
            formatted += tab.repeat(indent) + token.content + '\n'
            indent++
            inInline = false
          }
        } else if (token.type === 'close') {
          if (token.inline) {
            // Inline closing tag
            lineBuffer += token.content
            if (!nextToken || (!nextToken.inline && nextToken.type !== 'text')) {
              formatted += tab.repeat(indent) + lineBuffer + '\n'
              lineBuffer = ''
              inInline = false
            }
          } else {
            // Block closing tag
            indent = Math.max(0, indent - 1)
            if (lineBuffer) {
              formatted += tab.repeat(indent + 1) + lineBuffer + '\n'
              lineBuffer = ''
            }
            formatted += tab.repeat(indent) + token.content + '\n'
            inInline = false
          }
        } else if (token.type === 'self') {
          if (lineBuffer) {
            formatted += tab.repeat(indent) + lineBuffer + '\n'
            lineBuffer = ''
          }
          formatted += tab.repeat(indent) + token.content + '\n'
          inInline = false
        } else if (token.type === 'text') {
          if (inInline || (prevToken && prevToken.inline)) {
            lineBuffer += token.content
          } else {
            if (lineBuffer) {
              formatted += tab.repeat(indent) + lineBuffer + '\n'
              lineBuffer = ''
            }
            formatted += tab.repeat(indent) + token.content + '\n'
          }
        } else if (token.type === 'comment') {
          if (lineBuffer) {
            formatted += tab.repeat(indent) + lineBuffer + '\n'
            lineBuffer = ''
          }
          formatted += tab.repeat(indent) + token.content + '\n'
        }
      })
      
      if (lineBuffer) {
        formatted += tab.repeat(indent) + lineBuffer + '\n'
      }
      
      return formatted.trim()
    },
    
    // Source Code Toggle
    toggleSourceCode() {
      if (!this.editor) return
      
      if (this.isSourceCodeMode) {
        // Switching back to visual mode - update editor with source code
        this.editor.commands.setContent(this.sourceCode)
        this.isSourceCodeMode = false
      } else {
        // Switching to source code mode - get HTML from editor and format it
        const rawHTML = this.editor.getHTML()
        this.sourceCode = this.formatHTML(rawHTML)
        this.isSourceCodeMode = true
      }
    },
    
    updateFromSourceCode() {
      // Debounce updates to avoid performance issues
      if (this.sourceCodeUpdateTimeout) {
        clearTimeout(this.sourceCodeUpdateTimeout)
      }
      
      this.sourceCodeUpdateTimeout = setTimeout(() => {
        if (this.editor && this.isSourceCodeMode) {
          try {
            this.editor.commands.setContent(this.sourceCode)
          } catch (error) {
            console.warn('Invalid HTML in source code:', error)
          }
        }
      }, 500)
    },
    
    // Toolbar methods
    openLinkModal() {
      // Get current selection text and link attributes if editing
      const { from, to } = this.editor.state.selection
      const text = this.editor.state.doc.textBetween(from, to, '')
      const linkAttrs = this.editor.getAttributes('link')
      
      this.linkModalData = {
        text: text || '',
        url: linkAttrs.href || '',
        target: linkAttrs.target || ''
      }
      
      this.showLinkModal = true
    },
    
    clearFormatting() {
      this.executeCommand('unsetAllMarks')
      this.executeCommand('clearNodes')
    },
    
    // Modal handlers
    insertLink(linkData) {
      const { href, target, text } = linkData
      
      // If there's text provided and no current selection, insert new text with link
      if (text && this.editor.state.selection.empty) {
        this.editor
          .chain()
          .focus()
          .insertContent({
            type: 'text',
            text: text,
            marks: [
              {
                type: 'link',
                attrs: {
                  href,
                  target
                }
              }
            ]
          })
          .run()
      } else {
        // Otherwise just set the link on current selection
        this.executeCommand('setLink', { href, target })
      }
    },
    
    removeLink() {
      this.editor.chain().focus().unsetLink().run()
    },
    
    insertImage(imageData) {
      this.executeCommand('setResizableImage', imageData)
    },
    
    insertVideo(videoData) {
      this.executeCommand('setResizableVideo', videoData)
    }
  }
}
</script>
<style scoped>
.premium-editor-container {
  position: relative;
  border: 1px solid #e5e7eb;
  background: white;
}

.editor-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  background: #f9fafb;
  border-radius: 8px;
}

.editor-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 20px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  color: #991b1b;
}

.error-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

.error-content {
  text-align: center;
  max-width: 600px;
}

.error-content h3 {
  margin: 0 0 16px 0;
  font-size: 20px;
  font-weight: 600;
  color: #991b1b;
}

.missing-deps {
  text-align: left;
  background: white;
  padding: 20px;
  border-radius: 6px;
  border: 1px solid #fecaca;
  margin: 16px 0;
}

.missing-deps ul {
  margin: 8px 0;
  padding-left: 20px;
}

.missing-deps li {
  margin: 4px 0;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 13px;
}

.install-command {
  margin-top: 16px;
  padding: 12px;
  background: #f3f4f6;
  border-radius: 4px;
  border: 1px solid #d1d5db;
}

.install-command code {
  display: block;
  background: #1f2937;
  color: #f9fafb;
  padding: 12px;
  border-radius: 4px;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 12px;
  line-height: 1.4;
  word-break: break-all;
  white-space: pre-wrap;
  margin-top: 8px;
}

.generic-error {
  background: white;
  padding: 20px;
  border-radius: 6px;
  border: 1px solid #fecaca;
  margin: 16px 0;
}

.help-links {
  display: flex;
  gap: 16px;
  justify-content: center;
  margin-top: 20px;
}

.help-links a {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 8px 16px;
  background: #3b82f6;
  color: white;
  text-decoration: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  transition: background 0.2s ease;
}

.help-links a:hover {
  background: #2563eb;
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #e5e7eb;
  border-top: 3px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 12px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@keyframes pulse {
  0% { transform: translateX(-50%) scale(1); box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3); }
  50% { transform: translateX(-50%) scale(1.05); box-shadow: 0 6px 30px rgba(0, 0, 0, 0.4); }
  100% { transform: translateX(-50%) scale(1); box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3); }
}

.premium-editor-wrapper {
  display: flex;
  flex-direction: column;
  min-height: 200px;
}


.premium-editor-content {
  flex: 1;
  position: relative;
}

.bbl-editor-area {
  padding: 20px;
  outline: none;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  line-height: 1.6;
  color: #374151;
  overflow-y: auto;
}

/* Source Code Editor */
.source-code-editor {
  width: 100%;
  min-height: 400px;
  padding: 20px;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', 'Courier New', monospace;
  font-size: 13px;
  line-height: 1.6;
  color: #e2e8f0;
  background: #1e293b;
  border: none;
  outline: none;
  resize: vertical;
  overflow-y: auto;
  tab-size: 2;
  white-space: pre;
  overflow-wrap: normal;
  overflow-x: auto;
}

.source-code-editor::-webkit-scrollbar {
  width: 10px;
  height: 10px;
}

.source-code-editor::-webkit-scrollbar-track {
  background: #0f172a;
}

.source-code-editor::-webkit-scrollbar-thumb {
  background: #475569;
  border-radius: 5px;
}

.source-code-editor::-webkit-scrollbar-thumb:hover {
  background: #64748b;
}


.heading-select {
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  padding: 6px 8px;
  font-size: 13px;
  color: #374151;
  cursor: pointer;
  min-width: 120px;
  height: 32px;
}

.heading-select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
}

.color-picker-wrapper {
  position: relative;
  display: inline-block;
}

.color-picker {
  width: 32px;
  height: 32px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  cursor: pointer;
  background: transparent;
  padding: 0;
}

.color-picker::-webkit-color-swatch-wrapper {
  padding: 2px;
}

.color-picker::-webkit-color-swatch {
  border: none;
  border-radius: 2px;
}

.color-label {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 10px;
  font-weight: bold;
  color: white;
  text-shadow: 1px 1px 1px rgba(0, 0, 0, 0.8);
  pointer-events: none;
  z-index: 1;
}

/* Editor Content Styles */
.bbl-editor-area >>> .ProseMirror {
  outline: none;
}

.bbl-editor-area >>> .ProseMirror p.is-editor-empty:first-child::before {
  content: attr(data-placeholder);
  float: left;
  color: #9ca3af;
  pointer-events: none;
  height: 0;
}

.bbl-editor-area >>> h1,
.bbl-editor-area >>> h2,
.bbl-editor-area >>> h3,
.bbl-editor-area >>> h4,
.bbl-editor-area >>> h5,
.bbl-editor-area >>> h6 {
  font-weight: 600;
  line-height: 1.3;
  color: #111827;
}

.bbl-editor-area >>> h1 { font-size: 2em; }
.bbl-editor-area >>> h2 { font-size: 1.5em; }
.bbl-editor-area >>> h3 { font-size: 1.25em; }
.bbl-editor-area >>> h4 { font-size: 1.1em; }
.bbl-editor-area >>> h5 { font-size: 1em; }
.bbl-editor-area >>> h6 { font-size: 0.9em; }

.bbl-editor-area >>> p {
  margin: 0;
}

.bbl-editor-area >>> ul,
.bbl-editor-area >>> ol {
  margin: 1em 0;
  padding-left: 1.5em;
}

.bbl-editor-area >>> li {
  margin: 0.25em 0;
}

.bbl-editor-area >>> ul[data-type="taskList"] {
  list-style: none;
  padding-left: 0;
}

.bbl-editor-area >>> ul[data-type="taskList"] li {
  display: flex;
  align-items: flex-start;
  margin: 0.5em 0;
}

.bbl-editor-area >>> ul[data-type="taskList"] li > label {
  flex: 0 0 auto;
  margin-right: 0.5em;
  user-select: none;
}

.bbl-editor-area >>> ul[data-type="taskList"] li > div {
  flex: 1 1 auto;
}

/* Blockquotes - Styles handled by inline styles in CustomBlockquote extension */

.bbl-editor-area >>> code {
  background: #f1f5f9;
  padding: 0.2em 0.4em;
  border-radius: 3px;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 0.9em;
  color: #e11d48;
}

.bbl-editor-area >>> pre {
  background: #1e293b;
  color: #e2e8f0;
  padding: 1em;
  border-radius: 6px;
  overflow-x: auto;
  margin: 1.5em 0;
}

.bbl-editor-area >>> pre code {
  background: transparent;
  padding: 0;
  color: inherit;
}

.bbl-editor-area >>> hr {
  margin: 2em 0;
  border: none;
  border-top: 2px solid #e5e7eb;
}

.bbl-editor-area >>> .editor-link {
  color: #3b82f6;
  text-decoration: underline;
  cursor: pointer;
}

.bbl-editor-area >>> .editor-link:hover {
  color: #1d4ed8;
}

.bbl-editor-area >>> .editor-image {
  max-width: 100%;
  height: auto;
  border-radius: 4px;
}

/* Resizable Image Styles */
.bbl-editor-area >>> .resizable-image-container {
  position: relative;
  display: inline-block;
  max-width: 100%;
  margin: 1em 0;
}

.bbl-editor-area >>> .resizable-image-container:hover .resize-handle,
.bbl-editor-area >>> .resizable-image-container:hover .move-handle,
.bbl-editor-area >>> .resizable-image-container:hover .delete-button,
.bbl-editor-area >>> .resizable-image-container:hover .align-controls {
  opacity: 1 !important;
}

.bbl-editor-area >>> .resizable-image-container img {
  max-width: 100%;
  height: auto;
  display: block;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* Resizable Video Styles */
.bbl-editor-area >>> .resizable-video-container {
  position: relative;
  display: inline-block;
  margin: 1em 0;
  max-width: 100%;
  border-radius: 8px;
  overflow: hidden;
  
}
.bbl-editor-area >>> .resizable-video-container .resizable-video-wrapper {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.bbl-editor-area >>> .resizable-video-container:hover .video-resize-handle,
.bbl-editor-area >>> .resizable-video-container:hover .video-move-handle,
.bbl-editor-area >>> .resizable-video-container:hover .video-delete-button,
.bbl-editor-area >>> .resizable-video-container:hover .video-align-controls {
  opacity: 1 !important;
}

.bbl-editor-area >>> .video-resize-handle {
  position: absolute !important;
  bottom: 0px !important;
  right: 0px !important;
  width: 14px !important;
  height: 14px !important;
  background: #3b82f6 !important;
  cursor: se-resize !important;
  opacity: 0 !important;
  transition: opacity 0.2s ease !important;
  border-radius: 4px !important;
  z-index: 25 !important;
  border: 2px solid white !important;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.4) !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  color: white !important;
  font-size: 10px !important;
  font-weight: bold !important;
}

.bbl-editor-area >>> .video-move-handle {
  position: absolute !important;
  top: 8px !important;
  left: 8px !important;
  width: 32px !important;
  height: 32px !important;
  background: rgba(59, 130, 246, 0.95) !important;
  cursor: move !important;
  opacity: 0 !important;
  transition: opacity 0.2s ease !important;
  border-radius: 6px !important;
  z-index: 25 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  color: white !important;
  font-size: 14px !important;
  font-weight: bold !important;
  border: 2px solid white !important;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.4) !important;
}

.bbl-editor-area >>> .video-delete-button {
  position: absolute !important;
  top: 8px !important;
  right: 8px !important;
  width: 28px !important;
  height: 28px !important;
  background: rgba(239, 68, 68, 0.95) !important;
  cursor: pointer !important;
  opacity: 0 !important;
  transition: opacity 0.2s ease !important;
  border-radius: 4px !important;
  z-index: 25 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  color: white !important;
  font-size: 16px !important;
  font-weight: bold !important;
  border: 2px solid white !important;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.4) !important;
}

.bbl-editor-area >>> .video-align-controls {
  position: absolute !important;
  bottom: 8px !important;
  left: 8px !important;
  display: flex !important;
  gap: 4px !important;
  opacity: 0 !important;
  transition: opacity 0.2s ease !important;
  z-index: 25 !important;
}

/* Notion-style Table Enhancements */
.bbl-editor-area >>> .notion-table-wrapper {
  position: relative;
  display: inline-block;
  margin: 1em 0;
  border-radius: 8px;
  overflow: visible;
}

/* Cell Highlighting */
.bbl-editor-area >>> .highlighted-row {
  background-color: rgba(59, 130, 246, 0.05) !important;
  transition: background-color 0.15s ease;
}

.bbl-editor-area >>> .highlighted-column {
  background-color: rgba(59, 130, 246, 0.05) !important;
  transition: background-color 0.15s ease;
}

.bbl-editor-area >>> .highlighted-cell {
  background-color: rgba(59, 130, 246, 0.1) !important;
  transition: background-color 0.15s ease;
}

/* Table Cells */
.bbl-editor-area >>> table td,
.bbl-editor-area >>> table th {
  transition: background-color 0.15s ease;
  cursor: pointer;
}

.bbl-editor-area >>> table td:hover,
.bbl-editor-area >>> table th:hover {
  outline: 2px solid #7dacf8ff !important;
  border: 0px solid #7dacf8ff !important;
  outline-offset: -2px;
}

.bbl-editor-area >>> .notion-table-wrapper:hover .add-row-btn,
.bbl-editor-area >>> .notion-table-wrapper:hover .add-column-btn,
.bbl-editor-area >>> .notion-table-wrapper:hover .row-control,
.bbl-editor-area >>> .notion-table-wrapper:hover .column-control {
  opacity: 1 !important;
}

/* Inline Row Controls */
.bbl-editor-area >>> .row-control {
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s ease;
  z-index: 10;
}

.bbl-editor-area >>> .row-menu,
.bbl-editor-area >>> .column-menu {
  position: absolute;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  padding: 4px;
  display: none;
  flex-direction: column;
  gap: 2px;
  z-index: 20;
  min-width: 120px;
}

.bbl-editor-area >>> .row-menu div,
.bbl-editor-area >>> .column-menu div {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  cursor: pointer;
  border-radius: 4px;
  font-size: 12px;
  color: #374151;
  transition: background 0.15s ease;
}

.bbl-editor-area >>> .row-menu div:hover,
.bbl-editor-area >>> .column-menu div:hover {
  background: #f3f4f6;
}

/* Inline Column Controls */
.bbl-editor-area >>> .column-control {
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s ease;
  z-index: 10;
}

/* Add Buttons */
.bbl-editor-area >>> .add-row-btn,
.bbl-editor-area >>> .add-column-btn {
  position: absolute;
  width: 24px;
  height: 24px;
  background: #3b82f6;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  cursor: pointer;
  opacity: 0;
  transition: all 0.2s ease;
  font-size: 14px;
  font-weight: bold;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  z-index: 15;
  user-select: none;
}

.bbl-editor-area >>> .add-row-btn {
  bottom: -20px;
  left: 50%;
  transform: translateX(-50%);
}

.bbl-editor-area >>> .add-column-btn {
  right: -20px;
  top: 50%;
  transform: translateY(-50%);
}

.bbl-editor-area >>> .add-row-btn:hover,
.bbl-editor-area >>> .add-column-btn:hover {
  background: #2563eb;
  transform: scale(1.1);
}

.bbl-editor-area >>> .add-row-btn:hover {
  transform: translateX(-50%) scale(1.1);
}

.bbl-editor-area >>> .add-column-btn:hover {
  transform: translateY(-50%) scale(1.1);
}

/* Table Styles */
.bbl-editor-area >>> table {
  border-collapse: collapse;
  margin: 1em 0;
  width: 100%;
  border: 1px solid #d1d5db;
  table-layout: fixed;
}

.bbl-editor-area >>> th,
.bbl-editor-area >>> td {
  border: 1px solid #d1d5db;
  padding: 8px 12px;
  text-align: left;
  vertical-align: top;
  position: relative;
  min-width: 100px;
}

.bbl-editor-area >>> th {
  background: #f9fafb;
  font-weight: inherit;
}

.bbl-editor-area >>> .selectedCell {
  background: rgba(59, 130, 246, 0.1);
}

/* Bubble Menu Styles */
.bubble-menu {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  padding: 4px;
}

.bubble-menu-content {
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 2px;
}

.bubble-btn {
  background: white;
  border: 1px solid transparent;
  border-radius: 3px;
  padding: 4px 6px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  min-width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
  color: #374151;
}

.bubble-btn:hover {
  background: #f3f4f6;
  border-color: #d1d5db;
}

.bubble-btn.active {
  background: #3b82f6;
  color: white;
  border-color: #3b82f6;
}

.bubble-separator {
  width: 1px;
  height: 16px;
  background: #e5e7eb;
  margin: 0 4px;
}

/* Responsive Design */
@media (max-width: 768px) {
  .bbl-editor-area {
    padding: 16px;
  }
  
  .toolbar-content {
    gap: 4px;
  }
  
  .bbl-toolbar-group {
    margin: 1px;
  }
  
  .bbl-editor-area >>> h1 { font-size: 1.75em; }
  .bbl-editor-area >>> h2 { font-size: 1.5em; }
  .bbl-editor-area >>> h3 { font-size: 1.25em; }
}


.bbl-editor-area >>> .column-resize-handle {
  position: absolute;
  right: -2px;
  top: 0;
  bottom: 0;
  width: 4px;
  cursor: col-resize;
  z-index: 10;
  user-select: none;
  background-color: transparent;
  transition: background-color 0.15s ease;
}

.bbl-editor-area >>> .column-resize-handle:hover {
  background-color: #3b82f6;
}
</style>